/**
 * /prompt-compactor — Prompt Compactor dashboard.
 *
 * Authed light-theme page. Paste a prompt → see the before/after side-by-side,
 * token delta, $ saved. KPI strip shows cumulative savings for this tenant.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import BrandTitle from '../components/BrandTitle';
import { toast } from 'sonner';
import {
  Scissors, Zap, DollarSign, FileText, ArrowRight, Activity,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const MODELS = [
  'gpt-5.2',
  'gpt-5.2-mini',
  'claude-sonnet-4.5',
  'claude-haiku-4.5',
  'gemini-3-pro',
  'gemini-3-flash',
];

const SAMPLE = `You are a very helpful AI assistant. Please, in great detail, summarize the following document for our internal team in a way that captures all relevant points.

The document is essentially about our company's quarterly performance. Basically, our revenue grew by 23% year-over-year, driven primarily by strong enterprise sales. Operating margin improved from 18% to 22%. Cash position remains very strong at $142M.

Quarterly performance: Revenue grew 23% YoY, driven by enterprise sales. Operating margin improved from 18% to 22%. Cash position is $142M.

Please, please make the summary concise. Just give me the key points. Honestly, just three bullet points would be perfect.`;

const PromptCompactorPage = () => {
  const [prompt, setPrompt] = useState(SAMPLE);
  const [model, setModel] = useState('gpt-5.2-mini');
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchStats = async () => {
    try {
      const r = await axios.get(`${API}/prompt-compactor/stats`, auth());
      setStats(r.data);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const compact = async () => {
    if (!prompt.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/prompt-compactor/compact`, { prompt, model }, auth());
      setResult(r.data);
      toast.success(`Compacted ${r.data.tokens_saved} tokens (${(r.data.reduction_pct * 100).toFixed(1)}%)`);
      fetchStats();
    } catch (e) {
      toast.error('Compact failed');
    }
    setBusy(false);
  };

  const cumulativeSavings = (stats?.savings_usd || 0).toFixed(2);
  const reductionPct = ((stats?.reduction_pct || 0) * 100).toFixed(1);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 -mt-px">
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="prompt-compactor-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-white flex items-center gap-2">
              <Scissors className="w-7 h-7 text-emerald-400" />
              <BrandTitle>Prompt Compactor</BrandTitle>
            </h1>
            <p className="text-slate-200 text-sm mt-1">
              Strip filler, dedupe sentences, collapse whitespace — before the prompt hits the LLM. Engine #26.
            </p>
          </div>
          <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30 text-sm px-3 py-1">
            <Zap className="w-3.5 h-3.5 mr-1" /> {stats?.calls > 0 ? `${reductionPct}% avg reduction` : 'No compactions yet'}
          </Badge>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="compactor-kpis">
          {[
            { Icon: Activity, label: 'Compactions', value: (stats?.calls || 0).toLocaleString(), accent: 'emerald' },
            { Icon: FileText, label: 'Tokens saved', value: (stats?.tokens_saved || 0).toLocaleString(), accent: 'amber' },
            { Icon: Zap, label: 'Avg reduction', value: `${reductionPct}%`, accent: 'teal' },
            { Icon: DollarSign, label: 'Saved (USD)', value: `$${cumulativeSavings}`, accent: 'emerald' },
          ].map((k, i) => {
            const cls = {
              emerald: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
              amber: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
              teal: 'border-teal-500/30 bg-teal-500/10 text-teal-300',
            }[k.accent];
            const Ic = k.Icon;
            return (
              <Card key={i} className={`border ${cls}`}>
                <CardContent className="p-4">
                  <Ic className="w-5 h-5 mb-2 opacity-70" />
                  <div className="text-2xl font-extrabold tabular-nums">{k.value}</div>
                  <div className="text-xs uppercase tracking-wider mt-1 opacity-70">{k.label}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Input panel */}
        <Card className="border border-emerald-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Scissors className="w-5 h-5 text-emerald-500" />
                Compact a prompt
              </h2>
              <label className="text-sm text-slate-200 flex items-center gap-2">
                Model
                <select
                  data-testid="model-select"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="rounded-md border border-white/15 text-sm px-2 py-1 font-mono focus:border-emerald-500 focus:outline-none"
                >
                  {MODELS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
            </div>
            <textarea
              data-testid="prompt-textarea"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={8}
              className="w-full rounded-lg border border-white/15 px-3 py-2 text-sm font-mono focus:border-emerald-500 focus:ring focus:ring-emerald-200 focus:outline-none"
              placeholder="Paste any LLM prompt..."
            />
            <div className="flex justify-end mt-4">
              <Button onClick={compact} disabled={busy} data-testid="compact-btn" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
                {busy ? 'Compacting…' : <>Compact <ArrowRight className="w-4 h-4 ml-1.5" /></>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Result */}
        {result && (
          <div className="space-y-4" data-testid="compactor-result">
            {/* Stats banner */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl border border-white/10 bg-slate-900/60 p-4 text-center">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Original tokens</div>
                <div className="text-2xl font-extrabold text-white tabular-nums">{result.original_tokens}</div>
              </div>
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-center">
                <div className="text-xs uppercase tracking-wider text-emerald-300 mb-1">Compacted tokens</div>
                <div className="text-2xl font-extrabold text-emerald-300 tabular-nums">{result.compacted_tokens}</div>
              </div>
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-center">
                <div className="text-xs uppercase tracking-wider text-amber-300 mb-1">Saved</div>
                <div className="text-2xl font-extrabold text-amber-300 tabular-nums">
                  {result.tokens_saved} <span className="text-base">/ {(result.reduction_pct * 100).toFixed(1)}%</span>
                </div>
                <div className="text-xs text-amber-300/80 mt-1 tabular-nums">≈ ${result.estimated_savings_usd.toFixed(6)} / call</div>
              </div>
            </div>

            {/* Side-by-side diff */}
            <div className="grid lg:grid-cols-2 gap-4">
              <Card className="border border-white/10">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-bold text-white">Original</h3>
                    <Badge className="bg-slate-900/60 text-slate-200 border-white/10 font-mono text-[10px]">{result.original_tokens} tokens</Badge>
                  </div>
                  <pre className="text-xs leading-relaxed whitespace-pre-wrap font-mono text-slate-200 max-h-80 overflow-auto">{prompt}</pre>
                </CardContent>
              </Card>
              <Card className="border border-emerald-300">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-bold text-emerald-300">Compacted</h3>
                    <Badge className="bg-emerald-100 text-emerald-300 border-emerald-500/30 font-mono text-[10px]">{result.compacted_tokens} tokens</Badge>
                  </div>
                  <pre className="text-xs leading-relaxed whitespace-pre-wrap font-mono text-white max-h-80 overflow-auto">{result.compacted_prompt}</pre>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        <p className="text-xs text-slate-500 italic">
          Tip: in the LLM Proxy, set <code className="bg-slate-900/60 text-slate-100 px-1 py-0.5 rounded">X-Cartie-Compact: 1</code> in your request headers to auto-compact every prompt before forwarding.
        </p>
      </div>
    </div>
  );
};

export default PromptCompactorPage;
